/* eslint-disable no-undef */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { postProductCart, removeProductCart } from "../ongs.service";

import { useStore } from "@/store";

import type {
  ActionTypeProps,
  AddToCartProps,
  CartProduct,
  DebounceRequestAddToCartProps,
  DebounceRequestRemoveFromCartProps,
  RemoveToCartProps,
  Sku,
  UseFastBuyButtonProps
} from "../ongs.types";

export function useFastBuyButton({
  counter,
  firstClick,
  selectedSku,
  setCounter,
  setLoading,
  setFirstClick
}: UseFastBuyButtonProps) {
  const { updateOrderForm, orderForm, toogleOpen } = useStore();

  const wrapperButtonRef = useRef<HTMLDivElement | null>(null);
  const counterWrapperRef = useRef<HTMLDivElement | null>(null);

  const [loaderCheckout, setLoaderCheckout] = useState<string | null>(null);
  const [growAnimation, setGrowAnimation] = useState(false);
  const [debounceTimeoutId, setDebounceTimeoutId] =
    useState<NodeJS.Timeout | null>(null);

  const addToCart = useCallback(
    async ({ sku, counter }: AddToCartProps) => {
      setLoading(true);
      const quantity = counter + (firstClick ? 1 : 0);

      const payload = {
        id: sku.id,
        quantity,
        seller: sku.seller.id
      };

      try {
        if (!firstClick) {
          setLoaderCheckout(sku.id);
        }

        const products: CartProduct[] = [
          {
            seller: payload.seller || "",
            quantity: payload.quantity || 0,
            id: payload.id || ""
          }
        ];

        const response = await postProductCart({
          orderFormId: orderForm.orderFormId,
          products
        });
        updateOrderForm(response);
        toogleOpen();
      } catch {
        setFirstClick(false);
      } finally {
        setLoaderCheckout(null);
        setLoading(false);
      }
    },
    [
      firstClick,
      orderForm,
      setLoading,
      setLoaderCheckout,
      updateOrderForm,
      toogleOpen,
      setFirstClick
    ]
  );

  const removeFromCart = useCallback(
    async ({ sku, quantity }: RemoveToCartProps) => {
      setLoading(true);
      const index = 0;
      const payload = {
        id: sku.id,
        quantity: quantity - 1,
        seller: sku.seller.id,
        index
      };

      try {
        await removeProductCart({
          orderFormId: "",
          sellerId: payload.seller || "",
          quantity: payload.quantity,
          sku: payload.id,
          index: payload.index
        });
      } catch (error) {
        console.error("Error removing product from cart:", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const handleAddToCartEvent = async (sku: Sku) => {
    const { orderForm: currentOrderForm } = useStore.getState();

    window.gtag("event", "add_to_cart", {
      addtocart_method: "Doação",
      seller: currentOrderForm.sellers.map((seller) => seller.name),
      subscription: "Não",
      currency: "BRL",
      value: currentOrderForm.value / 100,
      items: currentOrderForm.items
        .filter((product) => {
          return product.id === sku.id;
        })
        .map((item) => {
          const categories = Object.values(item.productCategories);
          return {
            item_id: item.id,
            item_name: item.name.split(` ${item.skuName}`)[0],
            discount: item.priceTags.reduce((acc, tag) => {
              return (acc += tag.value);
            }, 0),
            affiliation: "Cobasi",
            item_brand: item.additionalInfo.brandName,
            item_category: categories[0] || "",
            item_category2: categories[1] || "",
            item_category3: categories[2] || "",
            item_variant: item.skuName,
            price: item.sellingPrice / 100,
            currency: "BRL",
            quantity: item.quantity
          };
        })
    });
  };

  const debounceRequestAddToCart = ({
    selectedSku,
    counter
  }: DebounceRequestAddToCartProps) => {
    if (debounceTimeoutId !== null) {
      clearTimeout(debounceTimeoutId);
    }

    const timeoutId = setTimeout(async () => {
      await addToCart({ sku: selectedSku, counter });
      handleAddToCartEvent(selectedSku);
    }, 1000);
    setDebounceTimeoutId(timeoutId);
  };

  const debounceRequestRemoveFromCart = ({
    selectedSku,
    quantity
  }: DebounceRequestRemoveFromCartProps) => {
    if (debounceTimeoutId !== null) {
      clearTimeout(debounceTimeoutId);
    }
    const timeoutId = setTimeout(async () => {
      await removeFromCart({ sku: selectedSku, quantity });
    }, 1000);
    setDebounceTimeoutId(timeoutId);
  };

  const changeCount = (action: ActionTypeProps) => {
    setCounter((prev) => (action === "add" ? prev + 1 : prev - 1));
  };

  const handleAddToCartClick = () => {
    wrapperButtonRef.current?.classList.add("active-animation");

    setTimeout(() => {
      setFirstClick(true);
      addToCart({ sku: selectedSku, counter });
    }, 500);
  };

  const handleRemoveCount = () => {
    changeCount("remove");
    debounceRequestRemoveFromCart({ selectedSku, quantity: counter });
  };

  const handleAddCount = () => {
    const productItem = orderForm.items.find(
      (item) => item.id === selectedSku.id
    );

    debounceRequestAddToCart({
      selectedSku,
      counter: productItem ? productItem.quantity + 1 : 1
    });
  };

  useEffect(() => {
    if (counter === 0) {
      counterWrapperRef.current?.classList.remove("active-wrapper");
      setGrowAnimation(true);
      setTimeout(() => {
        setCounter(1);
        setFirstClick(false);
      }, 500);
    }
  }, [counter, firstClick, setCounter, setFirstClick]);

  useEffect(() => {
    if (!loaderCheckout && firstClick) {
      counterWrapperRef.current?.classList.add("active-wrapper");
    }
  }, [loaderCheckout, firstClick]);

  return {
    refs: { wrapperButtonRef, counterWrapperRef },
    state: { loaderCheckout, growAnimation, firstClick },
    actions: { handleAddToCartClick, handleAddCount, handleRemoveCount }
  };
}
